import React, { Fragment, useEffect, useState } from 'react';
import {
    Row, Col, Input, Typography, Button, Form, Select, Switch,
    InputNumber, DatePicker, Upload, Collapse, Flex, Divider, Spin, Tag,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import UploadIcon from '../../components/icon/UploadIcon';
import openNotification from '../../components/notification';
import { createContract, updateContract, getContract, getCategoryOptions, getCustomFields, setIsSuccess, setContract } from './redux/slice';
import { primary } from '../../utils/theme/config';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_OPTIONS = [
    { value: 'active', label: 'Aktiv' },
    { value: 'cancelled', label: 'Gekündigt' },
    { value: 'expired', label: 'Abgelaufen' },
];

const INTERVAL_OPTIONS = [
    { value: 'monthly', label: 'Monatlich' },
    { value: 'quarterly', label: 'Quartalsweise' },
    { value: 'yearly', label: 'Jährlich' },
    { value: 'once', label: 'Einmalig' },
];

const FIELD_TYPE_LABELS = {
    text: 'Text',
    number: 'Zahl',
    date: 'Datum',
    boolean: 'Ja/Nein',
    select: 'Auswahl',
    multiselect: 'Mehrfachauswahl',
    currency: 'Währung (€)',
    url: 'URL',
    email: 'E-Mail',
    phone: 'Telefon',
};

const AddUpdateContract = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const auth = useSelector(state => state.auth);

    const contracts = useSelector(state => state.contracts);
    const { loading, isSuccess, contract, categoryOptions, customFields } = contracts;
    const organizer = auth?.decodeToken?.organizer;
    const userId = auth?.decodeToken?._id;

    const [fileList, setFileList] = useState([]);
    const [autoRenewal, setAutoRenewal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    useEffect(() => {
        if (organizer) {
            dispatch(getCategoryOptions(organizer));
        }
    }, [organizer]);

    useEffect(() => {
        if (isEdit && id) {
            dispatch(getContract(id));
        }
        return () => {
            dispatch(setContract(null));
            dispatch(setIsSuccess(false));
        };
    }, [id]);

    useEffect(() => {
        if (isEdit && contract) {
            const values = {
                ...contract,
                startDate: contract.startDate ? dayjs(contract.startDate) : null,
                endDate: contract.endDate ? dayjs(contract.endDate) : null,
                nextPaymentDate: contract.nextPaymentDate ? dayjs(contract.nextPaymentDate) : null,
                category: contract.category?._id || contract.category,
                tags: contract.tags || [],
            };
            // populate custom field values
            if (contract.customValues && Array.isArray(contract.customValues)) {
                contract.customValues.forEach((cv) => {
                    values[`cf_${cv.fieldKey}`] = cv.value;
                });
            }
            form.setFieldsValue(values);
            setAutoRenewal(contract.autoRenewal || false);
            const catId = contract.category?._id || contract.category;
            if (catId) {
                setSelectedCategory(catId);
                dispatch(getCustomFields({ category: catId, organizer }));
            }
            if (contract.files && contract.files.length > 0) {
                setFileList(contract.files.map((f, idx) => ({
                    uid: f._id || String(idx),
                    name: f.originalName || f.filename || `Datei ${idx + 1}`,
                    status: 'done',
                    url: `${process.env.REACT_APP_API_URI}/contracts/${f.filename || f}`,
                })));
            }
        }
    }, [contract]);

    useEffect(() => {
        if (isSuccess) {
            dispatch(setIsSuccess(false));
            navigate('/contract-manager/list');
        }
    }, [isSuccess]);

    const handleCategoryChange = (catId) => {
        setSelectedCategory(catId);
        // Clear existing custom field values
        const currentValues = form.getFieldsValue();
        const clearedValues = {};
        Object.keys(currentValues).forEach((k) => {
            if (k.startsWith('cf_')) clearedValues[k] = undefined;
        });
        form.setFieldsValue(clearedValues);
        if (catId && organizer) {
            dispatch(getCustomFields({ category: catId, organizer }));
        }
    };

    const handleOnFinish = async (values) => {
        const MAX_SIZE_MB = 10;
        for (const item of fileList) {
            if (item.originFileObj && item.originFileObj.size / 1024 / 1024 > MAX_SIZE_MB) {
                openNotification({ message: `${item.name} überschreitet die maximale Größe von ${MAX_SIZE_MB}MB.`, type: 'error' });
                return;
            }
        }

        const formData = new FormData();

        // Append files
        for (const item of fileList) {
            if (item.originFileObj) {
                formData.append('files', item.originFileObj);
            }
        }

        // Collect custom field values
        const customValues = [];
        (customFields || []).forEach((cf) => {
            const val = values[`cf_${cf.fieldKey}`];
            if (val !== undefined && val !== null && val !== '') {
                customValues.push({ fieldKey: cf.fieldKey, fieldName: cf.fieldName, value: val });
            }
            delete values[`cf_${cf.fieldKey}`];
        });

        // Append standard fields
        const skip = ['startDate', 'endDate', 'nextPaymentDate', 'files', 'tags'];
        Object.entries(values).forEach(([key, value]) => {
            if (skip.includes(key)) return;
            if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        });

        // Dates
        if (values.startDate) formData.append('startDate', values.startDate.toISOString());
        if (values.endDate) formData.append('endDate', values.endDate.toISOString());
        if (values.nextPaymentDate) formData.append('nextPaymentDate', values.nextPaymentDate.toISOString());

        // Tags
        if (values.tags && values.tags.length > 0) {
            values.tags.forEach((t) => formData.append('tags[]', t));
        }

        // Custom values
        formData.append('customValues', JSON.stringify(customValues));

        formData.append('user', userId);
        formData.append('organizer', organizer);

        if (isEdit) {
            formData.append('id', id);
            dispatch(updateContract({ formData }));
        } else {
            dispatch(createContract({ formData }));
        }
    };

    const onUploadChange = ({ file, fileList: newList }) => {
        if (file.size && file.size / 1024 / 1024 > 10) {
            openNotification({ message: `${file.name} überschreitet 10 MB.`, type: 'error' });
            return;
        }
        setFileList(newList);
    };

    const renderCustomField = (cf) => {
        const name = `cf_${cf.fieldKey}`;
        const label = cf.fieldName;
        const rules = cf.required ? [{ required: true, message: `${label} ist erforderlich` }] : [];

        switch (cf.fieldType) {
            case 'number':
            case 'currency':
                return (
                    <Form.Item key={cf.fieldKey} name={name} label={label} rules={rules}>
                        <InputNumber
                            style={{ width: '100%' }}
                            prefix={cf.fieldType === 'currency' ? '€' : undefined}
                            placeholder={label}
                        />
                    </Form.Item>
                );
            case 'date':
                return (
                    <Form.Item key={cf.fieldKey} name={name} label={label} rules={rules}>
                        <DatePicker style={{ width: '100%' }} format='DD.MM.YYYY' />
                    </Form.Item>
                );
            case 'boolean':
                return (
                    <Form.Item key={cf.fieldKey} name={name} label={label} valuePropName='checked' rules={rules}>
                        <Switch />
                    </Form.Item>
                );
            case 'select':
                return (
                    <Form.Item key={cf.fieldKey} name={name} label={label} rules={rules}>
                        <Select
                            placeholder={label}
                            options={(cf.options || []).map((o) => ({ value: o.value || o, label: o.label || o }))}
                        />
                    </Form.Item>
                );
            case 'multiselect':
                return (
                    <Form.Item key={cf.fieldKey} name={name} label={label} rules={rules}>
                        <Select
                            mode='multiple'
                            placeholder={label}
                            options={(cf.options || []).map((o) => ({ value: o.value || o, label: o.label || o }))}
                        />
                    </Form.Item>
                );
            case 'url':
                return (
                    <Form.Item key={cf.fieldKey} name={name} label={label} rules={[...rules, { type: 'url', message: 'Bitte gültige URL eingeben' }]}>
                        <Input placeholder='https://' />
                    </Form.Item>
                );
            case 'email':
                return (
                    <Form.Item key={cf.fieldKey} name={name} label={label} rules={[...rules, { type: 'email', message: 'Bitte gültige E-Mail eingeben' }]}>
                        <Input placeholder='name@beispiel.de' />
                    </Form.Item>
                );
            case 'phone':
                return (
                    <Form.Item key={cf.fieldKey} name={name} label={label} rules={rules}>
                        <Input placeholder='+49 ...' />
                    </Form.Item>
                );
            default:
                return (
                    <Form.Item key={cf.fieldKey} name={name} label={label} rules={rules}>
                        <Input placeholder={label} />
                    </Form.Item>
                );
        }
    };

    const collapseItems = [
        {
            key: '1',
            label: <Title level={5} type='success'>Grunddaten</Title>,
            children: (
                <Row gutter={16}>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item name='name' label='Vertragsname' rules={[{ required: true, message: 'Vertragsname ist erforderlich' }]}>
                            <Input placeholder='Vertragsname' />
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item name='category' label='Kategorie' rules={[{ required: true, message: 'Kategorie ist erforderlich' }]}>
                            <Select
                                showSearch
                                allowClear
                                placeholder='Kategorie wählen'
                                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                options={categoryOptions || []}
                                onChange={handleCategoryChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item name='status' label='Status' initialValue='active'>
                            <Select options={STATUS_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item name='partner' label='Vertragspartner / Anbieter'>
                            <Input placeholder='Partnername' />
                        </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                        <Form.Item name='contractNumber' label='Vertragsnummer'>
                            <Input placeholder='Vertragsnummer' />
                        </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                        <Form.Item name='customerNumber' label='Kundennummer'>
                            <Input placeholder='Kundennummer' />
                        </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                        <Form.Item name='contactPerson' label='Ansprechpartner'>
                            <Input placeholder='Ansprechpartner' />
                        </Form.Item>
                    </Col>
                </Row>
            ),
        },
        {
            key: '2',
            label: <Title level={5} type='success'>Laufzeit & Kündigung</Title>,
            children: (
                <Row gutter={16}>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item name='startDate' label='Startdatum'>
                            <DatePicker style={{ width: '100%' }} format='DD.MM.YYYY' placeholder='Startdatum' />
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item name='endDate' label='Enddatum'>
                            <DatePicker style={{ width: '100%' }} format='DD.MM.YYYY' placeholder='Enddatum' />
                        </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                        <Form.Item name='minimumTerm' label='Mindestlaufzeit (Monate)'>
                            <InputNumber style={{ width: '100%' }} min={0} placeholder='z.B. 12' />
                        </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                        <Form.Item name='noticePeriod' label='Kündigungsfrist (Tage)'>
                            <InputNumber style={{ width: '100%' }} min={0} placeholder='z.B. 30' />
                        </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                        <Form.Item name='autoRenewal' label='Automatische Verlängerung' valuePropName='checked'>
                            <Switch onChange={(val) => setAutoRenewal(val)} />
                        </Form.Item>
                    </Col>
                    {autoRenewal && (
                        <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                            <Form.Item name='renewalPeriod' label='Verlängerungszeitraum (Monate)'>
                                <InputNumber style={{ width: '100%' }} min={1} placeholder='z.B. 12' />
                            </Form.Item>
                        </Col>
                    )}
                </Row>
            ),
        },
        {
            key: '3',
            label: <Title level={5} type='success'>Kosten</Title>,
            children: (
                <Row gutter={16}>
                    <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                        <Form.Item name='costAmount' label='Kostenbetrag (€)'>
                            <InputNumber style={{ width: '100%' }} prefix='€' min={0} step={0.01} placeholder='0,00' />
                        </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                        <Form.Item name='costInterval' label='Kostenintervall'>
                            <Select allowClear placeholder='Intervall wählen' options={INTERVAL_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24}>
                        <Form.Item name='nextPaymentDate' label='Nächste Zahlung'>
                            <DatePicker style={{ width: '100%' }} format='DD.MM.YYYY' placeholder='Datum' />
                        </Form.Item>
                    </Col>
                </Row>
            ),
        },
        {
            key: '4',
            label: <Title level={5} type='success'>Verwaltung</Title>,
            children: (
                <Row gutter={16}>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item name='responsiblePerson' label='Zuständige Person'>
                            <Input placeholder='Name' />
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item name='location' label='Standort / Kostenstelle'>
                            <Input placeholder='Standort oder Kostenstelle' />
                        </Form.Item>
                    </Col>
                    <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                        <Form.Item name='tags' label='Tags'>
                            <Select mode='tags' placeholder='Tags hinzufügen' />
                        </Form.Item>
                    </Col>
                    <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                        <Form.Item name='notes' label='Notizen'>
                            <TextArea rows={4} placeholder='Notizen zum Vertrag...' />
                        </Form.Item>
                    </Col>
                </Row>
            ),
        },
        {
            key: '5',
            label: <Title level={5} type='success'>Dateien</Title>,
            children: (
                <Row gutter={16}>
                    <Col xs={24}>
                        <Form.Item name='files' valuePropName='fileList' getValueFromEvent={(e) => e?.fileList}>
                            <Upload.Dragger
                                multiple
                                showUploadList={false}
                                fileList={fileList}
                                onChange={onUploadChange}
                                beforeUpload={() => false}
                                accept='.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx'
                            >
                                <p className='ant-upload-drag-icon'>
                                    <UploadIcon size={30} />
                                </p>
                                <Text className='ant-upload-text' strong>Dateien hierher ziehen oder klicken</Text>
                                <p className='ant-upload-hint'>PDF, Bilder bis max. 10 MB</p>
                            </Upload.Dragger>
                        </Form.Item>
                        {fileList.length > 0 && (
                            <Flex vertical gap={8} className='padding-top-1'>
                                {fileList.map((file) => (
                                    <Flex key={file.uid} justify='space-between' align='center' style={{ padding: '4px 8px', background: '#f5f5f5', borderRadius: 6 }}>
                                        <Text ellipsis style={{ maxWidth: '80%' }}>
                                            {file.url ? (
                                                <a href={file.url} target='_blank' rel='noopener noreferrer'>{file.name}</a>
                                            ) : file.name}
                                        </Text>
                                        <Button
                                            type='link'
                                            danger
                                            size='small'
                                            onClick={() => setFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
                                        >
                                            Entfernen
                                        </Button>
                                    </Flex>
                                ))}
                            </Flex>
                        )}
                    </Col>
                </Row>
            ),
        },
        ...(selectedCategory && customFields && customFields.length > 0
            ? [{
                key: '6',
                label: <Title level={5} type='success'>Dynamische Felder</Title>,
                children: (
                    <Row gutter={16}>
                        {customFields.map((cf) => (
                            <Col key={cf.fieldKey} xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
                                {renderCustomField(cf)}
                            </Col>
                        ))}
                    </Row>
                ),
            }]
            : []),
    ];

    return (
        <Fragment>
            <Row justify='space-between' align='middle' className='bg-white padding-1 rounded-1' gutter={[16, 16]}>
                <Col>
                    <Title level={4} type='success'>{isEdit ? 'Vertrag bearbeiten' : 'Neuer Vertrag'}</Title>
                </Col>
                <Col>
                    <Button onClick={() => navigate('/contract-manager/list')}>Zurück zur Liste</Button>
                </Col>
            </Row>

            <div className='padding-top-1'>
                <Spin spinning={loading}>
                    <Form
                        form={form}
                        layout='vertical'
                        colon={false}
                        requiredMark={(label, isRule) => (
                            <Text>{label}<Text type='danger'>{isRule?.required && '*'}</Text></Text>
                        )}
                        onFinish={handleOnFinish}
                    >
                        <Collapse
                            items={collapseItems}
                            defaultActiveKey={['1', '2', '3', '4', '5', '6']}
                            className='w-100'
                            bordered={false}
                        />

                        <Divider />

                        <Row gutter={16} justify='end'>
                            <Col>
                                <Button
                                    danger
                                    type='primary'
                                    htmlType='button'
                                    onClick={() => { form.resetFields(); navigate('/contract-manager/list'); }}
                                >
                                    Abbrechen
                                </Button>
                            </Col>
                            <Col>
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    {isEdit ? 'Aktualisieren' : 'Speichern'}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Spin>
            </div>
        </Fragment>
    );
};

export default AddUpdateContract;
